/**
 * @fileoverview Flexible hover information card built on shadcn HoverCard.
 * @module shared/components/ui/HoverInfoCard
 */

import { ReactElement, ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

/**
 * Props for the HoverInfoCard wrapper.
 */
export interface HoverInfoCardProps {
  /** Trigger node shown in place; usually a card or icon */
  trigger: ReactNode
  /** Rich content displayed inside the hover card */
  children: ReactNode
  /** Optional content classes to tweak surface spacing/size */
  contentClassName?: string
  /** Preset width: 'one' = 357px, 'two' = 720px @default 'one' */
  size?: 'one' | 'two'
  /** Aligns the content relative to trigger @default center */
  align?: 'start' | 'center' | 'end'
  /** Side of the trigger to place the card */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Open delay in ms @default 200 */
  openDelay?: number
  /** Close delay in ms @default 100 */
  closeDelay?: number
}

/**
 * High-level wrapper around shadcn HoverCard with sensible defaults.
 *
 * @component
 * @example
 * ```tsx
 * <HoverInfoCard trigger={<button>Hover me</button>}>
 *   <HoverInfoHeader title="Sales" subtitle="Last 30 days" />
 *   <HoverInfoDivider />
 *   <HoverInfoRow label="Revenue" value="$120k" />
 *   <HoverInfoRow label="Orders" value="1,240" />
 * </HoverInfoCard>
 * ```
 */
export function HoverInfoCard({
  trigger,
  children,
  size = 'one',
  align = 'center',
  side,
  contentClassName,
  openDelay = 200,
  closeDelay = 100,
}: HoverInfoCardProps): ReactElement {
  const widthClass = size === 'two' ? 'w-[600px]' : 'w-[340px]'
  return (
    <HoverCard openDelay={openDelay} closeDelay={closeDelay}>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent
        align={align}
        {...(side ? { side } : {})}
        className={cn('glass-card !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] p-0 max-w-[95vw] overflow-hidden shadow-xl shadow-black/40', widthClass, contentClassName)}
      >
        {children}
      </HoverCardContent>
    </HoverCard>
  )
}

/**
 * Section header with optional title and subtitle.
 * Keeps styles minimal; compose freely.
 */
export interface HoverInfoHeaderProps {
  /** Main title text */
  title?: string
  /** Optional subtitle text (muted) */
  subtitle?: string
  /** Optional leading visual (icon/avatar) */
  leading?: ReactNode
  /** Optional classes for the leading wrapper (e.g. vertical alignment tweaks) */
  leadingClassName?: string
  /** Optional trailing node (badge/cta) */
  trailing?: ReactNode
  /** When set, the whole header becomes a link with a hover affordance */
  href?: string
  /** Additional classes */
  className?: string
  /** Use smaller title size for secondary headers */
  secondary?: boolean
}

/**
 * @component
 */
export function HoverInfoHeader({
  title,
  subtitle,
  leading,
  leadingClassName,
  trailing,
  href,
  className,
  secondary = false,
}: HoverInfoHeaderProps): ReactElement {
  const baseClassName = cn('flex items-center gap-4 px-6 py-4', !secondary && 'bg-black/30', className)
  const content = (
    <>
      {leading ? <div className={cn('shrink-0', leadingClassName)}>{leading}</div> : null}
      <div className="min-w-0 flex-1">
        {title ? (
          <p className={cn(secondary ? 'text-sm font-medium' : 'text-2xl font-semibold', 'text-foreground truncate')}>
            {title}
          </p>
        ) : null}
        {subtitle ? <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </>
  )
  if (href) {
    return (
      <Link
        to={href}
        className={cn(baseClassName, 'group cursor-pointer transition-colors duration-200 hover:bg-white/5')}
      >
        {content}
      </Link>
    )
  }
  return <div className={baseClassName}>{content}</div>
}

/** Generic label/value row. */
export interface HoverInfoRowProps {
  /** Row label */
  label: string
  /** Row value */
  value: ReactNode
  /** Optional subdued label */
  muted?: boolean
  /** Optional value color override (use tokens) */
  valueClassName?: string
  /** Optional leading icon/avatar next to label */
  leading?: ReactNode
  /** Custom classes */
  className?: string
  /** Compact size for smaller label and value text */
  compact?: boolean
}

/**
 * @component
 */
export function HoverInfoRow({
  label,
  value,
  muted,
  valueClassName,
  leading,
  className,
  compact = false,
}: HoverInfoRowProps): ReactElement {
  return (
    <div className={cn('flex items-center justify-between gap-8 px-6 py-1.5', className)}>
      <span className="flex items-center gap-2 min-w-0">
        {leading ? <span className="inline-flex shrink-0">{leading}</span> : null}
        <span
          className={cn(
            'text-xs truncate',
            muted ? 'text-muted-foreground' : 'text-foreground/80'
          )}
        >
          {label}
        </span>
      </span>
      <span className={cn(compact ? 'text-sm font-medium' : 'text-xl font-semibold', 'text-foreground', valueClassName)}>
        {value}
      </span>
    </div>
  )
}

/** Subtle divider for content grouping. */
export function HoverInfoDivider(): ReactElement {
  return <div className="h-px w-full bg-border" />
}

/** Compact stat block (value + label), good for grids. */
export interface HoverInfoStatProps {
  /** Primary numeric/text value */
  value: ReactNode
  /** Small caption label */
  label: string
  /** Optional value color override */
  valueClassName?: string
  /** Custom classes */
  className?: string
}

/**
 * @component
 */
export function HoverInfoStat({
  value,
  label,
  valueClassName,
  className,
}: HoverInfoStatProps): ReactElement {
  return (
    <div className={cn('flex flex-col px-6 py-3', className)}>
      <span className={cn('text-xl font-semibold text-foreground', valueClassName)}>{value}</span>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
    </div>
  )
}

/**
 * Product row with image, name, prominent value, and two meta fields.
 */
export interface HoverProductRowProps {
  /** Optional image URL (120x120 recommended) */
  imageUrl?: string | null | undefined
  /** Product title */
  title: string
  /** Optional badge rendered next to the title */
  badge?: ReactNode
  /** Primary prominent value (e.g., revenue/cost/profit) */
  value: ReactNode
  /** Optional color class for the value */
  valueClassName?: string
  /** Left meta text shown in tooltip (e.g., pcs sold) */
  leftMeta?: ReactNode
  /** Optional label for left meta text in tooltip */
  leftMetaLabel?: string
  /** Right meta text shown in tooltip (e.g., ad spent / cost per unit) */
  rightMeta?: ReactNode
  /** Optional label for right meta text in tooltip */
  rightMetaLabel?: string
  /** Optional additional footnote displayed beneath the title */
  footnote?: ReactNode
  /** Whether to dim quantity prefix (for '63 × Product Name' format) */
  dimQuantityPrefix?: boolean
  /** Compact mode with smaller image (55px instead of 65px) */
  compact?: boolean
  /** Click handler — when provided, the row becomes clickable */
  onClick?: () => void
  /** Additional CSS classes (use to override default padding when inside TableCell) */
  className?: string
}

export function HoverProductRow({
  imageUrl,
  title,
  badge,
  value,
  valueClassName,
  leftMeta,
  leftMetaLabel,
  rightMeta,
  rightMetaLabel,
  footnote,
  onClick,
  dimQuantityPrefix = false,
  compact = false,
  className,
}: HoverProductRowProps): ReactElement {
  const [imgError, setImgError] = useState(false)
  const shouldFallback = !imageUrl || imgError
  const initials = title ? title.trim().replace(/^\W+/, '').slice(0, 2).toUpperCase() : ''

  // Parse '63 × Product Name' format if dimQuantityPrefix is enabled
  const titleParts = dimQuantityPrefix && title.includes(' × ') ? title.split(' × ') : null

  // Prepare tooltip content if meta information exists
  const hasMetaInfo = leftMeta || rightMeta
  const tooltipContent = hasMetaInfo ? (
    <div className="space-y-1">
      {leftMeta && leftMetaLabel && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{leftMetaLabel}:</span>
          <span className="text-sm text-foreground">{leftMeta}</span>
        </div>
      )}
      {rightMeta && rightMetaLabel && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{rightMetaLabel}:</span>
          <span className="text-sm text-foreground">{rightMeta}</span>
        </div>
      )}
      {leftMeta && !leftMetaLabel && (
        <div className="text-sm text-foreground">{leftMeta}</div>
      )}
      {rightMeta && !rightMetaLabel && (
        <div className="text-sm text-foreground">{rightMeta}</div>
      )}
    </div>
  ) : null

  const contentElement = (
    <>
      <div className="flex flex-col items-start gap-1">
        <p className="w-full min-w-0 text-xs font-normal leading-snug line-clamp-2 text-muted-foreground">
          {titleParts ? (
            <>
              <span className="text-muted-foreground/75">{titleParts[0]} ×</span>
              <span className="text-foreground/90 ml-1">{titleParts[1]}</span>
            </>
          ) : (
            title
          )}
        </p>
        {badge ? <span className="shrink-0">{badge}</span> : null}
        <div className={cn('mt-0.5 font-semibold text-foreground text-base', valueClassName)}>
          {value}
        </div>
      </div>
      {footnote ? (
        <div className="mt-2 text-xs text-muted-foreground line-clamp-2">{footnote}</div>
      ) : null}
    </>
  )

  const wrappedContent = hasMetaInfo ? (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="min-w-0 flex-1 cursor-pointer">
          {contentElement}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-card/95 border border-white/10 shadow-2xl rounded-xl px-4 py-2.5 backdrop-blur" hideArrow>
        {tooltipContent}
      </TooltipContent>
    </Tooltip>
  ) : (
    <div className="min-w-0 flex-1">
      {contentElement}
    </div>
  )

  const imageSize = compact ? 'h-[48px] w-[48px]' : 'h-[55px] w-[55px]'

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
      className={cn('flex items-start w-full px-6 py-2.5 border-b border-border/30 last:border-b-0', compact ? 'gap-3' : 'gap-4', onClick && 'cursor-pointer hover:bg-white/5 transition-colors', className)}
    >
      <div className={cn(imageSize, 'rounded-md bg-muted/20 overflow-hidden shrink-0')}>
        {shouldFallback ? (
          <div className="w-full h-full flex items-center justify-center text-lg font-medium text-muted-foreground">
            {initials}
          </div>
        ) : (
          <img
            src={imageUrl!}
            alt={title}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        )}
      </div>
      {wrappedContent}
    </div>
  )
}
